import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { PublicNavBar } from '../components/PublicNavBar'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { getPackage, listPackageVersions, uploadPackageIcon, deletePackageIcon, getDownloadSparkline, updatePackage, deletePackage, markPackageOfficial } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import { usePageMeta } from '../hooks/usePageMeta'
import { tryPageMeta } from '../seo/pageMeta'
import type { Package, PackageVersion, PackageArtifact } from '../types/packages'

const OS_LABELS: Record<string, string> = {
  linux: 'Linux',
  darwin: 'macOS',
  windows: 'Windows',
}

const ARCH_LABELS: Record<string, string> = {
  amd64: 'x86_64',
  arm64: 'ARM64',
  '386': 'x86',
}

function formatBytes(n: number): string {
  if (!n) return ''
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${n} B`
}

function formatDownloads(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

function ArtifactButton({ artifact, slug, version }: { artifact: PackageArtifact; slug: string; version: string }) {
  const downloadURL = `/api/packages/${slug}/versions/${version}/download/${artifact.name}`
  const osLabel = OS_LABELS[artifact.os] ?? artifact.os
  const archLabel = ARCH_LABELS[artifact.arch] ?? artifact.arch

  return (
    <a
      href={downloadURL}
      className="flex items-center gap-3 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all group"
    >
      <svg className="w-5 h-5 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {osLabel} {archLabel && <span className="font-normal text-gray-500 dark:text-gray-400">— {archLabel}</span>}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{artifact.name}</p>
      </div>
      {artifact.size_bytes > 0 && (
        <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
          {formatBytes(artifact.size_bytes)}
        </span>
      )}
    </a>
  )
}

function VersionSection({ version, slug, isLatest }: { version: PackageVersion; slug: string; isLatest: boolean }) {
  const [expanded, setExpanded] = useState(isLatest)

  const byOS: Record<string, PackageArtifact[]> = {}
  for (const artifact of version.artifacts ?? []) {
    const os = artifact.os || 'other'
    if (!byOS[os]) byOS[os] = []
    byOS[os].push(artifact)
  }
  const osList = Object.keys(byOS).sort()

  return (
    <div className={`border rounded-xl overflow-hidden transition-colors ${version.yanked ? 'border-yellow-300 dark:border-yellow-700 opacity-60' : 'border-gray-200 dark:border-gray-700'}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-900 dark:text-gray-100">v{version.version}</span>
          {isLatest && (
            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded-full font-medium">
              Latest
            </span>
          )}
          {version.yanked && (
            <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 rounded-full font-medium">
              Yanked
            </span>
          )}
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {new Date(version.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-5 pb-5 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
          {version.changelog && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 mb-4 whitespace-pre-wrap">
              {version.changelog}
            </p>
          )}
          {version.artifacts?.length > 0 ? (
            <div className="space-y-3">
              {osList.map((os) => (
                <div key={os}>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    {OS_LABELS[os] ?? os}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {byOS[os].map((a) => (
                      <ArtifactButton key={a.name} artifact={a} slug={slug} version={version.version} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">No artifacts published for this version.</p>
          )}
        </div>
      )}
    </div>
  )
}

function DownloadSparkline({ slug }: { slug: string }) {
  const [data, setData] = useState<Array<{ date: string; count: number }>>([])
  useEffect(() => {
    getDownloadSparkline(slug).then(setData).catch(() => {})
  }, [slug])

  if (!data.length) return null
  const max = Math.max(...data.map((d) => d.count), 1)

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">Downloads (30d)</h3>
      <div className="flex items-end gap-px h-12">
        {data.map((d) => {
          const pct = max > 0 ? (d.count / max) * 100 : 0
          return (
            <div
              key={d.date}
              className="flex-1 bg-primary-500 dark:bg-primary-400 rounded-sm opacity-80 hover:opacity-100 transition-opacity"
              style={{ height: `${Math.max(pct, 2)}%` }}
              title={`${d.date}: ${d.count} downloads`}
            />
          )
        })}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-xs text-gray-400 dark:text-gray-500">{data[0]?.date.slice(5)}</span>
        <span className="text-xs text-gray-400 dark:text-gray-500">{data[data.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  )
}

type InstallTab = 'cli' | 'curl' | 'go'

function InstallCommands({ pkg }: { pkg: Package }) {
  const [tab, setTab] = useState<InstallTab>('cli')
  const [copied, setCopied] = useState(false)

  const allTabs: Array<{ id: InstallTab; label: string; show: boolean }> = [
    { id: 'cli', label: 'CLI', show: true },
    { id: 'curl', label: 'curl', show: true },
    { id: 'go', label: 'Go', show: pkg.language === 'go' },
  ]
  const tabs = allTabs.filter((t) => t.show)

  const commands: Record<InstallTab, string> = {
    cli: `factory packages download ${pkg.slug}`,
    curl: `curl -fsSL https://factory-nexus.ai/install/${pkg.slug} | sh`,
    go: `go install github.com/${pkg.slug}@latest`,
  }

  function copy() {
    navigator.clipboard.writeText(commands[tab]).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">Install</h3>
      <div className="flex gap-1 mb-3">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              tab === t.id
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2 font-mono text-xs text-gray-800 dark:text-gray-200">
        <span className="text-gray-400 select-none">$</span>
        <span className="flex-1 truncate">{commands[tab]}</span>
        <button onClick={copy} title="Copy" className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
          {copied ? (
            <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
            </svg>
          )}
        </button>
      </div>
    </div>
  )
}

interface EditForm {
  name: string
  description: string
  author: string
  language: string
  tags: string
  readme: string
  is_public: boolean
}

export default function PackageDetail() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { user, isAuthenticated, permissions } = useAuth()
  const isAdmin = permissions.isAdmin

  const [pkg, setPkg] = useState<Package | null>(null)
  const [versions, setVersions] = useState<PackageVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [readmeOpen, setReadmeOpen] = useState(false)

  // Icon state
  const [iconLoadFailed, setIconLoadFailed] = useState(false)
  const [iconUploading, setIconUploading] = useState(false)
  const [iconError, setIconError] = useState<string | null>(null)
  const iconInputRef = useRef<HTMLInputElement>(null)

  // Edit modal
  const [editOpen, setEditOpen] = useState(false)
  const [editForm, setEditForm] = useState<EditForm>({ name: '', description: '', author: '', language: '', tags: '', readme: '', is_public: true })
  const [editing, setEditing] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Delete
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Admin mark official
  const [markingOfficial, setMarkingOfficial] = useState(false)

  const isOwner = !!user && !!pkg && (user.id === pkg.owner_id)
  const canEdit = isOwner || isAdmin

  // The official packages have hand-tuned meta in pageMeta.json (the same copy
  // the prerenderer writes into the initial HTML) — prefer it over the generic
  // API-derived fallback so client-side navigation doesn't undo it.
  const curated = tryPageMeta(`/packages/${slug ?? ''}`)
  usePageMeta(
    curated ?? {
      title: pkg ? `${pkg.name} — Factory Nexus Packages` : 'Factory Nexus',
      description: pkg?.description || 'Download packages and developer tools from the Factory Nexus package registry.',
      path: `/packages/${slug ?? ''}`,
    },
  )

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    Promise.all([getPackage(slug), listPackageVersions(slug)])
      .then(([p, v]) => {
        setPkg(p)
        setVersions(v ?? [])
      })
      .catch(() => setError('Package not found.'))
      .finally(() => setLoading(false))
  }, [slug])

  // Reset icon failed state when URL changes
  useEffect(() => { setIconLoadFailed(false) }, [pkg?.icon_url])

  function openEdit() {
    if (!pkg) return
    setEditForm({
      name: pkg.name,
      description: pkg.description ?? '',
      author: pkg.author ?? '',
      language: pkg.language ?? '',
      tags: pkg.tags?.join(', ') ?? '',
      readme: pkg.readme ?? '',
      is_public: pkg.is_public,
    })
    setEditError(null)
    setEditOpen(true)
  }

  async function handleEdit(e: React.FormEvent) {
    e.preventDefault()
    if (!slug) return
    setEditing(true)
    setEditError(null)
    try {
      const updated = await updatePackage(slug, {
        name: editForm.name.trim() || undefined,
        description: editForm.description.trim() || undefined,
        author: editForm.author.trim() || undefined,
        language: editForm.language.trim() || undefined,
        tags: editForm.tags ? editForm.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        readme: editForm.readme.trim() || undefined,
        is_public: editForm.is_public,
      })
      setPkg(updated)
      setEditOpen(false)
    } catch {
      setEditError('Failed to update package.')
    } finally {
      setEditing(false)
    }
  }

  async function handleDelete() {
    if (!slug) return
    setDeleting(true)
    try {
      await deletePackage(slug)
      navigate('/packages')
    } catch {
      setDeleteConfirm(false)
      setDeleting(false)
    }
  }

  async function handleMarkOfficial(official: boolean) {
    if (!slug) return
    setMarkingOfficial(true)
    try {
      await markPackageOfficial(slug, official)
      setPkg((prev) => prev ? { ...prev, is_official: official } : prev)
    } catch {
      // ignore
    } finally {
      setMarkingOfficial(false)
    }
  }

  async function handleIconUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !slug) return
    setIconUploading(true)
    setIconError(null)
    try {
      const result = await uploadPackageIcon(slug, file)
      setPkg((prev) => prev ? { ...prev, icon_url: result.icon_url } : prev)
    } catch {
      setIconError('Failed to upload icon. Max 1MB, PNG/JPEG/SVG/WebP.')
    } finally {
      setIconUploading(false)
      if (iconInputRef.current) iconInputRef.current.value = ''
    }
  }

  async function handleIconDelete() {
    if (!slug) return
    setIconUploading(true)
    setIconError(null)
    try {
      await deletePackageIcon(slug)
      setPkg((prev) => prev ? { ...prev, icon_url: undefined } : prev)
    } catch {
      setIconError('Failed to remove icon.')
    } finally {
      setIconUploading(false)
    }
  }

  function copyInstall() {
    const cmd = `factory install ${slug}`
    navigator.clipboard.writeText(cmd).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        {!isAuthenticated && <PublicNavBar />}
        <div className="flex justify-center py-24"><LoadingSpinner size="lg" /></div>
      </div>
    )
  }

  if (error || !pkg) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        {!isAuthenticated && <PublicNavBar />}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4">{error ?? 'Package not found.'}</p>
          <Link to="/packages" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
            ← Back to packages
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {!isAuthenticated && <PublicNavBar />}

      {/* Edit modal */}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Package</h2>
              <button onClick={() => setEditOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-4">
              {editError && <p className="text-sm text-red-600 dark:text-red-400">{editError}</p>}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Author</label>
                <input
                  value={editForm.author}
                  onChange={(e) => setEditForm((f) => ({ ...f, author: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Language</label>
                <input
                  value={editForm.language}
                  onChange={(e) => setEditForm((f) => ({ ...f, language: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags <span className="font-normal text-gray-400">(comma-separated)</span></label>
                <input
                  value={editForm.tags}
                  onChange={(e) => setEditForm((f) => ({ ...f, tags: e.target.value }))}
                  placeholder="cli, tool, automation"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">README</label>
                  <label className="cursor-pointer text-xs text-primary-600 dark:text-primary-400 hover:underline">
                    Load from file
                    <input
                      type="file"
                      accept=".md,.txt"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        const reader = new FileReader()
                        reader.onload = (ev) => setEditForm((f) => ({ ...f, readme: String(ev.target?.result ?? '') }))
                        reader.readAsText(file)
                        e.target.value = ''
                      }}
                    />
                  </label>
                </div>
                <textarea
                  value={editForm.readme}
                  onChange={(e) => setEditForm((f) => ({ ...f, readme: e.target.value }))}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={editForm.is_public}
                  onChange={(e) => setEditForm((f) => ({ ...f, is_public: e.target.checked }))}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="is_public" className="text-sm text-gray-700 dark:text-gray-300">Public (visible in registry)</label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditOpen(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editing}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-500 disabled:opacity-50 transition-colors"
                >
                  {editing ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Delete package?</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              This will permanently delete <span className="font-medium text-gray-700 dark:text-gray-300">{pkg.slug}</span> and all its versions. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-500 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Back + actions */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/packages" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Packages
          </Link>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button
                onClick={openEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
              <button
                onClick={() => setDeleteConfirm(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header card */}
            <div className={`bg-white dark:bg-gray-900 border rounded-xl p-6 ${pkg.is_official ? 'border-orange-400 dark:border-orange-500 ring-1 ring-orange-400 dark:ring-orange-500' : 'border-gray-200 dark:border-gray-700'}`}>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 relative group">
                  <div className="w-16 h-16 rounded-xl bg-gray-100 dark:bg-gray-800 overflow-hidden flex items-center justify-center">
                    {pkg.icon_url && !iconLoadFailed ? (
                      <img
                        src={pkg.icon_url}
                        alt={pkg.name}
                        className="w-full h-full object-cover"
                        onError={() => setIconLoadFailed(true)}
                      />
                    ) : (
                      <span className="text-3xl font-bold text-gray-400 dark:text-gray-600 uppercase select-none">
                        {pkg.slug[0]}
                      </span>
                    )}
                    {iconUploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
                        <LoadingSpinner size="sm" />
                      </div>
                    )}
                  </div>
                  {isOwner && !iconUploading && (
                    <div className="absolute -bottom-1 -right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => iconInputRef.current?.click()}
                        title="Upload icon"
                        className="w-6 h-6 bg-primary-600 hover:bg-primary-700 text-white rounded-full flex items-center justify-center shadow transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                      </button>
                      {pkg.icon_url && (
                        <button
                          onClick={handleIconDelete}
                          title="Remove icon"
                          className="w-6 h-6 bg-gray-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={handleIconUpload}
                  />
                </div>
                {iconError && (
                  <p className="text-xs text-red-500 mt-1 absolute">{iconError}</p>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{pkg.slug}</h1>
                    {pkg.is_official && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 rounded-full">
                        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="currentColor">
                          <path d="M6 1l1.545 3.13 3.455.502-2.5 2.436.59 3.44L6 8.882 2.91 10.508l.59-3.44L1 4.632l3.455-.502L6 1z" />
                        </svg>
                        Official
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">{pkg.description}</p>
                  {pkg.author && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      by <span className="font-medium text-gray-700 dark:text-gray-300">{pkg.author}</span>
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {pkg.language && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                        {pkg.language}
                      </span>
                    )}
                    {pkg.tags?.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Install command */}
              <div className="mt-5 flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3 font-mono text-sm text-gray-800 dark:text-gray-200">
                <span className="text-gray-400 select-none">$</span>
                <span className="flex-1">factory install {pkg.slug}</span>
                <button
                  onClick={copyInstall}
                  title="Copy to clipboard"
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  {copied ? (
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* README */}
            {pkg.readme && (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                <button
                  onClick={() => setReadmeOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
                >
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">README</h2>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${readmeOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {readmeOpen && (
                  <div className="px-6 pb-6 border-t border-gray-100 dark:border-gray-800 pt-5">
                    <div className="prose prose-sm dark:prose-invert max-w-none
                      prose-headings:font-semibold prose-headings:text-gray-900 dark:prose-headings:text-gray-100
                      prose-p:text-gray-700 dark:prose-p:text-gray-300
                      prose-a:text-primary-600 dark:prose-a:text-primary-400
                      prose-strong:text-gray-900 dark:prose-strong:text-gray-100
                      prose-blockquote:border-l-gray-300 dark:prose-blockquote:border-l-gray-600 prose-blockquote:text-gray-600 dark:prose-blockquote:text-gray-400
                      prose-hr:border-gray-200 dark:prose-hr:border-gray-700
                      prose-li:text-gray-700 dark:prose-li:text-gray-300">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          pre({ children }) {
                            return (
                              <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto my-4 text-sm">
                                {children}
                              </pre>
                            )
                          },
                          code({ className, children }) {
                            const isBlock = Boolean(className)
                            return isBlock ? (
                              <code className={`text-gray-100 font-mono text-sm ${className ?? ''}`}>
                                {children}
                              </code>
                            ) : (
                              <code className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1 py-0.5 rounded text-xs font-mono before:content-none after:content-none">
                                {children}
                              </code>
                            )
                          },
                        }}
                      >
                        {pkg.readme}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Versions */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Versions <span className="text-sm font-normal text-gray-400 dark:text-gray-500">({versions.length})</span>
              </h2>
              {versions.length === 0 ? (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                  {isAuthenticated && user?.id === pkg.owner_id ? (
                    <div className="space-y-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Publish your first version</p>
                      <div className="space-y-3">
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Via CLI</p>
                          <code className="text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
                            factory packages publish {pkg.slug}@1.0.0 \<br />
                            {'  '}--artifact ./dist/{pkg.slug}-linux-amd64 --os linux --arch amd64
                          </code>
                        </div>
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Via workflow step</p>
                          <code className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre">{`- name: Publish
  type: publish_package
  package: ${pkg.slug}
  version: "1.0.0"`}</code>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-center text-sm text-gray-400 dark:text-gray-500">No versions published yet.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {versions.map((v) => (
                    <VersionSection
                      key={v.id}
                      version={v}
                      slug={pkg.slug}
                      isLatest={v.version === pkg.latest_version}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Install commands */}
            <InstallCommands pkg={pkg} />

            {/* Stats + sparkline */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">Stats</h3>
                <dl className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <dt className="text-gray-500 dark:text-gray-400">Downloads</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">{formatDownloads(pkg.total_downloads)}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-gray-500 dark:text-gray-400">Latest</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">{pkg.latest_version ? `v${pkg.latest_version}` : '—'}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-gray-500 dark:text-gray-400">Versions</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">{versions.length}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-gray-500 dark:text-gray-400">Published</dt>
                    <dd className="font-medium text-gray-900 dark:text-gray-100">
                      {new Date(pkg.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </dd>
                  </div>
                </dl>
              </div>
              <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
                <DownloadSparkline slug={pkg.slug} />
              </div>
            </div>

            {/* Admin controls */}
            {isAdmin && (
              <div className="bg-white dark:bg-gray-900 border border-orange-200 dark:border-orange-800/50 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-orange-700 dark:text-orange-400 mb-3 uppercase tracking-wide">Admin</h3>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Official package</span>
                  <button
                    onClick={() => handleMarkOfficial(!pkg.is_official)}
                    disabled={markingOfficial}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${pkg.is_official ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${pkg.is_official ? 'translate-x-4' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              </div>
            )}

            {/* Publish CTA for visitors */}
            {!isAuthenticated && (
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border border-primary-200 dark:border-primary-700/50 rounded-xl p-5">
                <p className="text-sm font-medium text-primary-800 dark:text-primary-200 mb-1">Publish your own package</p>
                <p className="text-xs text-primary-600 dark:text-primary-400 mb-3">Share tools with the Factory community.</p>
                <button
                  onClick={() => navigate('/register')}
                  className="w-full px-3 py-2 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-500 transition-colors"
                >
                  Get Started
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
